import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useHouse, useHouseSettings, useUpdateHouse } from '../../../platform/api/hooks';
import { asRecord, str } from '../../../platform/api/utils';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';
import {
  type HouseForm,
  STATUS_LABELS,
  STATUS_VARIANTS,
  MODULE_LABELS,
  houseFormSchema,
  DEFAULT_MODULES,
} from './house-settings.support';

export function HouseSettings() {
  const { data: house, isLoading, isError, error } = useHouse();
  const { data: settings } = useHouseSettings();
  const updateHouse = useUpdateHouse();
  const [editing, setEditing] = useState(false);

  const h = asRecord(house);
  const s = asRecord(settings);

  const form = useForm<HouseForm>({
    resolver: zodResolver(houseFormSchema),
    values: {
      name: str(h.name),
      publicName: str(h.publicName),
      legalName: str(h.legalName),
      documentNumber: str(h.documentNumber),
      email: str(h.email),
      phone: str(h.phone),
      city: str(h.city),
      state: str(h.state),
      addressLine: str(h.addressLine),
    },
  });

  if (isLoading) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
        <PageHeader title="Casa" subtitle="Dados institucionais" />
        <Skeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
        <PageHeader title="Casa" subtitle="Dados institucionais" />
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: 0 }}>
            {error instanceof Error ? error.message : 'Não foi possível carregar os dados da casa.'}
          </p>
        </Section>
      </div>
    );
  }

  const status = str(h.status, 'setup');
  const statusLabel = STATUS_LABELS[status] ?? status;
  const rawModules = s.enabledModules;
  let parsedModules: string[] = [];
  if (typeof rawModules === 'string') {
    try {
      parsedModules = JSON.parse(rawModules) as string[];
    } catch {
      /* keep empty */
    }
  } else if (Array.isArray(rawModules)) {
    parsedModules = rawModules as string[];
  }
  const activeModules = parsedModules.length > 0 ? parsedModules : [...DEFAULT_MODULES];
  const locale = str(s.locale, 'pt-BR');
  const timezone = str(s.timezone, 'America/Bahia');

  function handleSave(data: HouseForm) {
    updateHouse.mutate(data);
    setEditing(false);
  }

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <PageHeader
        title="Casa"
        subtitle="Dados institucionais e configurações"
        action={
          editing
            ? {
                label: updateHouse.isPending ? 'Salvando...' : 'Salvar',
                onClick: () => {
                  void form.handleSubmit(handleSave)();
                },
              }
            : {
                label: 'Editar',
                onClick: () => {
                  setEditing(true);
                },
              }
        }
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Header */}
        <Section title="Identidade">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>
                {str(h.publicName ?? h.name, '—')}
              </h2>
              <Badge label={statusLabel} variant={STATUS_VARIANTS[status] ?? 'default'} />
            </div>
            {str(h.name) !== str(h.publicName) && (
              <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                Nome interno: {str(h.name)}
              </span>
            )}
          </div>
        </Section>

        {/* Dados institucionais */}
        <Section
          title="Dados institucionais"
          action={
            !editing ? null : (
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  padding: '0.25rem 0.75rem',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  color: 'var(--muted)',
                }}
              >
                Cancelar
              </button>
            )
          }
        >
          {editing ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem' }}>
              <Field label="Nome interno" error={form.formState.errors.name?.message}>
                <input {...form.register('name')} style={inputStyle} />
              </Field>
              <Field label="Nome público" error={form.formState.errors.publicName?.message}>
                <input {...form.register('publicName')} style={inputStyle} />
              </Field>
              <Field label="Nome jurídico">
                <input {...form.register('legalName')} style={inputStyle} />
              </Field>
              <Field label="CNPJ/CPF">
                <input
                  {...form.register('documentNumber')}
                  style={inputStyle}
                  placeholder="00.000.000/0001-00"
                />
              </Field>
            </div>
          ) : (
            <dl style={dlGrid}>
              <dt style={dlTerm}>Nome interno</dt>
              <dd style={dlDesc}>{str(h.name)}</dd>
              <dt style={dlTerm}>Nome público</dt>
              <dd style={dlDesc}>{str(h.publicName)}</dd>
              <dt style={dlTerm}>Nome jurídico</dt>
              <dd style={dlDesc}>{str(h.legalName, '—')}</dd>
              <dt style={dlTerm}>CNPJ/CPF</dt>
              <dd style={dlDesc}>{str(h.documentNumber, '—')}</dd>
            </dl>
          )}
        </Section>

        {/* Contato */}
        <Section title="Contato">
          {editing ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem' }}>
              <Field label="E-mail" error={form.formState.errors.email?.message}>
                <input
                  {...form.register('email')}
                  type="email"
                  style={inputStyle}
                  placeholder="casa@exemplo.com"
                />
              </Field>
              <Field label="Telefone">
                <input
                  {...form.register('phone')}
                  style={inputStyle}
                  placeholder="(75) 99999-0000"
                />
              </Field>
            </div>
          ) : (
            <dl style={dlGrid}>
              <dt style={dlTerm}>E-mail</dt>
              <dd style={dlDesc}>{str(h.email, '—')}</dd>
              <dt style={dlTerm}>Telefone</dt>
              <dd style={dlDesc}>{str(h.phone, '—')}</dd>
            </dl>
          )}
        </Section>

        {/* Localização */}
        <Section title="Localização">
          {editing ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem' }}>
              <Field label="Cidade">
                <input
                  {...form.register('city')}
                  style={inputStyle}
                  placeholder="Feira de Santana"
                />
              </Field>
              <Field label="UF">
                <input
                  {...form.register('state')}
                  maxLength={2}
                  placeholder="BA"
                  style={{ ...inputStyle, maxWidth: 80 }}
                />
              </Field>
              <div style={{ gridColumn: 'span 2' }}>
                <Field label="Endereço">
                  <input
                    {...form.register('addressLine')}
                    style={inputStyle}
                    placeholder="Rua Principal, 123"
                  />
                </Field>
              </div>
            </div>
          ) : (
            <dl style={dlGrid}>
              <dt style={dlTerm}>Cidade</dt>
              <dd style={dlDesc}>{str(h.city, '—')}</dd>
              <dt style={dlTerm}>Estado</dt>
              <dd style={dlDesc}>{str(h.state, '—')}</dd>
              <dt style={dlTerm}>Endereço</dt>
              <dd style={dlDesc}>{str(h.addressLine, '—')}</dd>
            </dl>
          )}
        </Section>

        {/* Configurações */}
        <Section title="Configurações">
          <dl style={dlGrid}>
            <dt style={dlTerm}>Idioma</dt>
            <dd style={dlDesc}>{localeMap(locale)}</dd>
            <dt style={dlTerm}>Fuso horário</dt>
            <dd style={dlDesc}>{timezone}</dd>
            <dt style={dlTerm}>Status</dt>
            <dd style={dlDesc}>
              <Badge label={statusLabel} variant={STATUS_VARIANTS[status] ?? 'default'} />
            </dd>
          </dl>
        </Section>

        {/* Módulos ativos */}
        <Section title="Módulos ativos">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {activeModules.map((mod: string) => {
              const label = MODULE_LABELS[mod] ?? mod;
              return (
                <span
                  key={mod}
                  style={{
                    background: 'var(--success-soft)',
                    color: 'var(--success)',
                    padding: '0.3rem 0.7rem',
                    borderRadius: 20,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  }}
                >
                  {label}
                </span>
              );
            })}
          </div>
        </Section>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem' }}>
      <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{label}</span>
      {children}
      {error && <span style={{ color: 'var(--danger)', fontSize: '0.7rem' }}>{error}</span>}
    </label>
  );
}

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid var(--line)',
            padding: '1.25rem 1.5rem',
            height: 120,
          }}
        >
          <div
            style={{
              width: '40%',
              height: 14,
              background: 'var(--line-soft)',
              borderRadius: 8,
              marginBottom: '0.75rem',
            }}
          />
          <div
            style={{
              width: '70%',
              height: 12,
              background: 'var(--soft)',
              borderRadius: 8,
              marginBottom: '0.5rem',
            }}
          />
          <div style={{ width: '50%', height: 12, background: 'var(--soft)', borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );
}

const dlGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '160px 1fr',
  gap: '0.5rem 1rem',
  fontSize: '0.875rem',
};

const dlTerm: React.CSSProperties = {
  color: 'var(--muted)',
  fontWeight: 500,
  margin: 0,
};

const dlDesc: React.CSSProperties = {
  margin: 0,
  color: 'var(--ink)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.85rem',
  boxSizing: 'border-box',
  outline: 'none',
};

function localeMap(locale: string): string {
  const map: Record<string, string> = {
    'pt-BR': 'Português (Brasil)',
    en: 'English',
    es: 'Español',
  };
  return map[locale] ?? locale;
}
